import React, { Component } from 'react';
import PropTypes from 'prop-types';
import KeyboardSmall from 'components/keyboard-small';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import { padEnd } from 'lodash';
import { DEFAULT_PIN } from 'config';
import { StyledBlock, StyledModalContainer, StyledModalTitle } from '../StyledComponents';
import ActionsSection from '../ActionsSection';
import i18n from 'i18n';
import { StyledPinInputChar } from 'components/Modal/StyledComponents';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';

const MAX_PIN_LENGTH = 4;
const PIN_CODE_CHAR_PLACEHOLDER = '_';

export default class EnterPinModal extends Component {
  state = {
    pin: '',
    isPinValid: true,
    isKeyboardVisible: true,
  };

  componentDidUpdate(prevProps, prevState) {
    const { error } = this.props;
    const { error: prevError } = prevProps;

    if (!prevError && error) {
      this.setState({ isPinValid: false });
    }

    if (prevError && !error) {
      this.setState({ isPinValid: true });
    }
  }

  onBack = () => {
    const { history } = this.props;
    history.goBack();
  };

  showKeyboard = () => {
    this.setState({ isKeyboardVisible: true, isPinValid: true }, () => {
      SpatialNavigationWrapper.focus('@KEYBOARD_NUM').set('PIN_NUMBER_INPUT', {
        leaveFor: {
          right: '@KEYBOARD_NUM',
          up: '',
          left: '',
          down: '',
        },
      });
    });
  };

  hideKeyboard = () => {
    SpatialNavigationWrapper.set('PIN_NUMBER_INPUT', {
      leaveFor: {
        right: '',
        up: '',
        left: '',
        down: '',
      },
    });
    this.setState({ isKeyboardVisible: false });
  };

  onKeyboardInput = (value) => {
    const { checkOnComplete, error, setPinError } = this.props;

    if (error) setPinError(null);

    if (value === 'remove') {
      this.setState((state) => ({ pin: state.pin.slice(0, -1), isPinValid: true }));
    } else if (value === 'close') {
      this.setState({ isKeyboardVisible: false, isPinValid: this.isPinComplete() }, () => {
        this.handleConfirmation();
        SpatialNavigationWrapper.focus('@PIN_NUMBER_INPUT');
      });
    } else if (!this.isPinComplete()) {
      this.setState(
        (state) => ({ pin: state.pin + value }),
        () => {
          if (checkOnComplete && this.isPinComplete()) {
            this.handleConfirmation();
          }
        },
      );
    }
  };

  handleConfirmation = () => {
    const { checkPin, setPin, type, onModalConfirm } = this.props;
    const { pin } = this.state;

    if (this.isPinComplete()) {
      if (type === 'createPin') {
        setPin(pin, DEFAULT_PIN, onModalConfirm);
      } else {
        checkPin({ pin, onModalConfirm });
      }
    } else {
      this.setState({ isPinValid: false });
    }
  };

  restorePin = () => {
    const { onModalBack, gotToRestorePinPage } = this.props;

    innerAnalyticsVolia.postEvent({
      eventGroup: 'notification',
      eventName: 'PIN request',
      eventValue: 'click credentials recovery',
    });

    onModalBack();
    gotToRestorePinPage();
  };

  isPinComplete = () => this.state.pin.length === MAX_PIN_LENGTH;

  renderPinCodeChar = (char, idx) => {
    const cursorPosition = this.state.pin.length;
    const isPlaceholder = char === PIN_CODE_CHAR_PLACEHOLDER;
    const isActive = idx === cursorPosition;

    return (
      <StyledPinInputChar key={idx} isActive={isActive} isPlaceholder={isPlaceholder}>
        {char}
      </StyledPinInputChar>
    );
  };

  render() {
    const { isKeyboardVisible, isPinValid, pin } = this.state;
    const { onModalBack, modalTitleText, type, error } = this.props;
    const { showKeyboard, hideKeyboard } = this;
    const cursorPosition = pin.length;

    return (
      <StyledModalContainer>
        <StyledModalTitle>{modalTitleText}</StyledModalTitle>
        <StyledBlock>
          <ActionsSection
            sectionName="PIN_NUMBER_INPUT"
            defaultSection
            config={{
              leaveFor: {
                right: '@KEYBOARD_NUM',
                up: '',
                left: '',
                down: '',
              },
            }}
          >
            <ActionsSection.Input
              value={padEnd(pin, MAX_PIN_LENGTH, PIN_CODE_CHAR_PLACEHOLDER)}
              isValid={isPinValid}
              invalidMessage={error || i18n.t('pc.pinError')}
              cursor={isKeyboardVisible}
              cursorPosition={cursorPosition}
              onEnter={showKeyboard}
              renderCharacter={this.renderPinCodeChar}
            />
            {type === 'enterPin' && (
              <ActionsSection.Button label={i18n.t('pc.restore')} onEnter={this.restorePin} onFocus={hideKeyboard} />
            )}
            <ActionsSection.Button label={i18n.t('pc.cancel')} onEnter={onModalBack} onFocus={hideKeyboard} />
          </ActionsSection>
          {isKeyboardVisible && (
            <KeyboardSmall
              className="keyboard-small__pin"
              onEnter={this.onKeyboardInput}
              config={{
                leaveFor: {
                  left: '@PIN_NUMBER_INPUT',
                  right: '',
                  up: '',
                  down: '',
                },
              }}
            />
          )}
        </StyledBlock>
      </StyledModalContainer>
    );
  }
}

EnterPinModal.defaultProps = {
  checkOnComplete: false,
  type: 'enterPin',
};

EnterPinModal.propTypes = {
  onModalBack: PropTypes.func.isRequired,
  onModalConfirm: PropTypes.func,
  modalTitleText: PropTypes.string.isRequired,
  checkOnComplete: PropTypes.bool,
};
